import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Universal grade-tier badge (Task #111).
 *
 * Renders a category-agnostic "high / mid / low" pill so every storefront
 * scale (A/B+/B−, Tier 1/2/3, etc.) reads the same way at a glance:
 *   - high → green
 *   - mid  → amber
 *   - low  → red
 *
 * The legacy `<GradeBadge>` is preserved for code-specific styling on the
 * cart, PDP, and pallet flows; this is the new tier-style pill used on the
 * /showroom and /category pages.
 */
export type GradeTier = "high" | "mid" | "low" | "refurb";

interface Props {
  code: string;
  label?: string;
  tier: GradeTier;
  className?: string;
  compact?: boolean;
}

const TIER_CLASSES: Record<GradeTier, string> = {
  high: "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-200 dark:border-green-700",
  mid: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700",
  low: "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700",
  refurb: "bg-emerald-100 text-emerald-800 border-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-700",
};

export function GradeTierBadge({ code, label, tier, className, compact = false }: Props) {
  const size = compact ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-semibold border whitespace-nowrap",
        size,
        TIER_CLASSES[tier],
        className,
      )}
      data-testid={`grade-tier-${tier}-${code}`}
      title={label || code}
    >
      {label || code}
    </span>
  );
}

/**
 * Pre-built badge for SharkNinja Grade X / "Factory Refurbished" pallets.
 * Used on order detail, invoice PDF surfaces (where supported), and admin
 * pallet drill-in views to give the customer a clear, consistent label.
 */
export function GradeXBadge({ className, compact = false }: { className?: string; compact?: boolean }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid="grade-x-badge-trigger">
            <GradeTierBadge
              code="X"
              label="Factory Refurbished (Grade X)"
              tier="refurb"
              className={className}
              compact={compact}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          Fully factory refurbished — may have minor scuffs or blemishes.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
